/**
 * excelClient — read an uploaded Excel workbook (.xlsx / .xls / .xlsm / .csv)
 * into the SAME raw 2D `values` array shape the Google-Sheets path produces
 * (`importSheetValues`), so it feeds the identical pipeline:
 *   readExcelValues(file) → parsePrelimAssignments(values) → toSheetDraftGrid
 *
 * No network, no Firebase, no AI — pure client-side parse via SheetJS. The
 * anesthesia department exports the daily "Prelim Anes Assignments" Google Sheet
 * to .xlsx; the first worksheet carries the A–F layout the parser expects.
 *
 * Returns a ragged 2D array of STRINGS (matching the Sheets API output): blank
 * cells become "" and every value is stringified, so downstream column reads
 * (norm()) behave exactly as with the live sheet.
 */

/**
 * Parse an uploaded spreadsheet File into a 2D string array (first sheet).
 * @param {File} file  the uploaded .xlsx/.xls/.xlsm/.csv file
 * @returns {Promise<string[][]>}
 */
export async function readExcelValues(file) {
  if (!file) throw new Error("No file provided.");

  // Lazy-load SheetJS (~100 KB gzip) only when an import actually runs, so it
  // ships as its own chunk instead of bloating the main bundle.
  const XLSX = await import("xlsx");

  // ArrayBuffer is supported by all modern browsers + Capacitor WebViews.
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });

  const firstName = wb.SheetNames?.[0];
  if (!firstName) throw new Error("The workbook has no sheets.");
  const ws = wb.Sheets[firstName];
  if (!ws) throw new Error("Could not read the first worksheet.");

  // header:1 → array-of-arrays (row-major), same as the Sheets values array.
  // raw:false → formatted text (times like "730 - 1445" stay strings, not serials).
  // defval:"" → keep blank cells as "" so column indices don't shift.
  const rows = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    raw: false,
    defval: "",
    blankrows: true,
  });

  return rows.map((r) =>
    Array.isArray(r) ? r.map((c) => (c == null ? "" : String(c))) : []
  );
}
